import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TOAST_DURATION_MS } from '../constants.ts';
import buttons from '../styles/buttons.module.css';
import { Icon } from './Icon.tsx';
import styles from './Toast.module.css';

// Short notices that confirm a write landed ("Saved") or, for the few
// actions with no form to show it in, that one failed. They stack in a
// polite live region so a screen reader hears them without being
// interrupted, dismiss themselves after a moment, and can be closed early.
// The provider holds the list; the app places the region inside its status
// landmark, so the notices are page content like everything else.

export type ToastTone = 'info' | 'error';

interface Toast {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

export interface ToastApi {
  /** Show `message`; an `error` tone is styled as a failure. */
  readonly notify: (message: string, tone?: ToastTone) => void;
}

interface ToastList {
  readonly toasts: readonly Toast[];
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const ToastListContext = createContext<ToastList | null>(null);

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((previous) => previous.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((previous) => [...previous, { id, message, tone }]);
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id);
        }, TOAST_DURATION_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) {
        clearTimeout(timer);
      }
      pending.clear();
    };
  }, []);

  const api = useMemo(() => ({ notify }), [notify]);
  const list = useMemo(() => ({ toasts, dismiss }), [toasts, dismiss]);

  return (
    <ToastContext value={api}>
      <ToastListContext value={list}>{children}</ToastListContext>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error('useToast must be used inside ToastProvider');
  }
  return context;
}

/** The live region the notices appear in; rendered once, inside a landmark. */
export function ToastRegion() {
  const list = useContext(ToastListContext);
  if (list === null) {
    throw new Error('ToastRegion must be used inside ToastProvider');
  }
  return (
    <div className={styles['region']} aria-live="polite">
      {list.toasts.map((toast) => (
        <div key={toast.id} className={styles['toast']} data-tone={toast.tone}>
          <span className={styles['message']}>{toast.message}</span>
          <button
            className={`${buttons['ghost'] ?? ''} ${buttons['icon'] ?? ''}`}
            type="button"
            aria-label={`Dismiss: ${toast.message}`}
            onClick={() => {
              list.dismiss(toast.id);
            }}
          >
            <Icon name="x" size="1rem" />
          </button>
        </div>
      ))}
    </div>
  );
}
